export type ItemBadge =
  | "chef_pick"
  | "signature"
  | "award"
  | "homemade"
  | "best_seller"
  | "fan_favorite"
  | "viral"
  | "seasonal"
  | "limited_time"
  | "new"
  | "today_only"
  | "vegan"
  | "gluten_free"
  | "low_cal"

export type BadgeCategory = "featured" | "urgency" | "quality" | "dietary"

export interface BadgeMeta {
  label:     string
  icon:      string
  category:  BadgeCategory
  color:     string   // hex background
  hasExpiry: boolean  // whether to show date pickers in admin
  special:   boolean  // shimmer animation on menu board
}

export const BADGE_META: Record<ItemBadge, BadgeMeta> = {
  // ── Featured ──────────────────────────────────────────────────────────
  chef_pick:    { label: "Chef's Pick",      icon: "👨‍🍳", category: "featured", color: "#f59e0b", hasExpiry: false, special: true  },
  signature:    { label: "Firma del chef",   icon: "⭐",   category: "featured", color: "#e8342a", hasExpiry: false, special: false },
  award:        { label: "Premiado",         icon: "🏆",   category: "featured", color: "#a855f7", hasExpiry: false, special: false },
  best_seller:  { label: "Más pedido",       icon: "🔥",   category: "featured", color: "#ef4444", hasExpiry: false, special: true  },
  fan_favorite: { label: "Favorito",         icon: "❤️",  category: "featured", color: "#ec4899", hasExpiry: false, special: false },

  // ── Urgency / Tiempo ──────────────────────────────────────────────────
  new:          { label: "Nuevo",            icon: "✨",   category: "urgency",  color: "#3b82f6", hasExpiry: false, special: true  },
  seasonal:     { label: "De temporada",     icon: "🍂",   category: "urgency",  color: "#f97316", hasExpiry: true,  special: true  },
  limited_time: { label: "Tiempo limitado",  icon: "⏰",   category: "urgency",  color: "#f59e0b", hasExpiry: true,  special: true  },
  today_only:   { label: "Solo hoy",         icon: "📅",   category: "urgency",  color: "#8b5cf6", hasExpiry: false, special: false },
  viral:        { label: "Viral",            icon: "📱",   category: "urgency",  color: "#06b6d4", hasExpiry: false, special: false },

  // ── Calidad ───────────────────────────────────────────────────────────
  homemade:     { label: "Casero",           icon: "🏠",   category: "quality",  color: "#78716c", hasExpiry: false, special: false },

  // ── Dieta ─────────────────────────────────────────────────────────────
  vegan:        { label: "Vegano",           icon: "🌱",   category: "dietary",  color: "#22c55e", hasExpiry: false, special: false },
  gluten_free:  { label: "Sin gluten",       icon: "🌾",   category: "dietary",  color: "#84cc16", hasExpiry: false, special: false },
  low_cal:      { label: "Bajo en calorías", icon: "💚",   category: "dietary",  color: "#10b981", hasExpiry: false, special: false },
}

export const BADGE_CATEGORIES: { key: BadgeCategory; label: string; badges: ItemBadge[] }[] = [
  { key: "featured", label: "Destacado",       badges: ["chef_pick", "signature", "award", "best_seller", "fan_favorite"] },
  { key: "urgency",  label: "Disponibilidad",  badges: ["new", "seasonal", "limited_time", "today_only", "viral"] },
  { key: "quality",  label: "Calidad",         badges: ["homemade"] },
  { key: "dietary",  label: "Dieta",           badges: ["vegan", "gluten_free", "low_cal"] },
]

export const ALL_BADGES = Object.keys(BADGE_META) as ItemBadge[]

export function isBadgeActive(
  badge: string | null | undefined,
  badgeEndsAt: string | null | undefined,
): boolean {
  if (!badge) return false
  if (!badgeEndsAt) return true
  return new Date(badgeEndsAt) > new Date()
}

export function resolveBadge(
  badge: string | null | undefined,
  badgeEndsAt: string | null | undefined,
): BadgeMeta | null {
  if (!isBadgeActive(badge, badgeEndsAt)) return null
  return BADGE_META[badge as ItemBadge] ?? null
}
